import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = '';

// فك تشفير JWT لقراءة الدور
function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch { return null; }
}

function isAdminFromToken() {
  const token = localStorage.getItem('token');
  if (token) {
    const decoded = decodeToken(token);
    if (decoded?.role === 'admin') return true;
  }
  return localStorage.getItem('user_role') === 'admin';
}

// تجديد التوكن كل ساعة لضمان بقاء الجلسة نشطة
const REFRESH_INTERVAL_MS = 1 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const mountedRef = useRef(false);

  // مسح الجلسة بالكامل (بدون alert - يستخدم sessionStorage للرسائل)
  const clearSession = useCallback((reason) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user_role');
    setToken(null);
    setUser(null);
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (reason) {
      sessionStorage.setItem('logout_reason', reason);
    }
  }, []);

  // دالة تسجيل الخروج (يدوي أو إجباري)
  const logout = useCallback((message) => {
    clearSession(message);
  }, [clearSession]);

  // بدء مؤقت التجديد التلقائي
  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    refreshTimerRef.current = setInterval(async () => {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) return;

      try {
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` },
          timeout: 10000
        });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        localStorage.setItem('user_role', userData?.role || 'user');
        setToken(access_token);
        setUser(userData);
      } catch (error) {
        if (error.response?.status === 401) {
          const detail = error.response?.data?.detail || '';
          if (isAdminFromToken()) return; // الأدمن لا يُطرد أبداً
          if (detail.includes('جهاز آخر')) {
            clearSession('تم تسجيل الدخول من جهاز آخر. سجّل دخول مجدداً لاستعادة الجلسة.');
          }
          // التوكن انتهى: لا تطرد فوراً - الـ interceptor في api.js يتولى الأمر
        }
        // أخطاء الشبكة: تجاهل وانتظر المحاولة القادمة
      }
    }, REFRESH_INTERVAL_MS);
  }, [clearSession]);

  // إعادة تحميل بيانات المستخدم (بعد الدفع مثلاً)
  const refreshUser = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        timeout: 10000
      });
      setUser(response.data);
      localStorage.setItem('user_role', response.data?.role || 'user');
    } catch (error) {
      if (error.response?.status === 401) {
        const detail = error.response?.data?.detail || '';
        if (isAdminFromToken()) return;
        if (detail.includes('جهاز آخر')) {
          clearSession('تم تسجيل الدخول من جهاز آخر. سجّل دخول مجدداً لاستعادة الجلسة.');
        }
      }
    }
  }, [clearSession]);

  // تحميل بيانات المستخدم عند بدء التطبيق (مرة واحدة فقط)
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          timeout: 10000
        });
        setUser(response.data);
        localStorage.setItem('user_role', response.data?.role || 'user');
        startRefreshTimer();
      } catch (error) {
        if (error.response?.status === 401) {
          const detail = error.response?.data?.detail || '';
          const isAdmin = isAdminFromToken();

          if (isAdmin) {
            // الأدمن: حاول التجديد، لا تطرده أبداً
            try {
              const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
                headers: { Authorization: `Bearer ${storedToken}` },
                timeout: 10000
              });
              const { access_token, user: userData } = refreshRes.data;
              localStorage.setItem('token', access_token);
              localStorage.setItem('user_role', userData?.role || 'user');
              setToken(access_token);
              setUser(userData);
              startRefreshTimer();
            } catch {
              // الأدمن يبقى - سيحاول مجدداً
            }
          } else if (detail.includes('جهاز آخر')) {
            // مستخدم عادي: طُرد من جهاز آخر
            clearSession(detail);
          } else {
            // مستخدم عادي: التوكن انتهت - حاول التجديد مرة
            try {
              const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
                headers: { Authorization: `Bearer ${storedToken}` },
                timeout: 10000
              });
              const { access_token, user: userData } = refreshRes.data;
              localStorage.setItem('token', access_token);
              localStorage.setItem('user_role', userData?.role || 'user');
              setToken(access_token);
              setUser(userData);
              startRefreshTimer();
            } catch {
              clearSession('انتهت جلستك. سجّل دخول مجدداً للمتابعة.');
            }
          }
        }
        // أخطاء الشبكة: ابقِ التوكن وحاول لاحقاً
      }
      setLoading(false);
    };

    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // تجديد التوكن عند عودة المستخدم للتبويب
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const currentToken = localStorage.getItem('token');
      if (!currentToken) return;

      try {
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` },
          timeout: 10000
        });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        localStorage.setItem('user_role', userData?.role || 'user');
        setToken(access_token);
        setUser(userData);
      } catch (error) {
        if (error.response?.status === 401) {
          if (isAdminFromToken()) return;
          const detail = error.response?.data?.detail || '';
          if (detail.includes('جهاز آخر')) {
            clearSession('تم تسجيل الدخول من جهاز آخر. سجّل دخول مجدداً لاستعادة الجلسة.');
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [clearSession]);

  // تنظيف المؤقت عند إزالة المكون
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []);

  // تسجيل مستخدم جديد
  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, userData);
      const { access_token, user: userData2 } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user_role', userData2?.role || 'user');
      setToken(access_token);
      setUser(userData2);
      startRefreshTimer();
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'حدث خطأ أثناء التسجيل';
      return { success: false, error: message };
    }
  };

  // تسجيل الدخول
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user_role', userData?.role || 'user');
      setToken(access_token);
      setUser(userData);
      startRefreshTimer();
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'البريد الإلكتروني أو كلمة المرور غير صحيحة';
      return { success: false, error: message };
    }
  };

  // التحقق من حالة تسجيل الدخول
  const isAuthenticated = !!user && !!token;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated,
      register,
      login,
      logout,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
